import { Vector3 } from "three";
import {
  IInputEventHandler,
  InputEventType,
  InputMultiPointerEvent,
  InputPointerType,
  IPinchArgs,
  ISwipeArgs,
} from "../utils/input-handler/interface";
import { Viewer } from "../viewer";
import { InputHandler } from "../utils/input-handler/input-handler";

export class CoordTracker implements IInputEventHandler {
  private _viewer: Viewer;
  private _inputHandler: InputHandler;
  private _callback: (res: Vector3) => void;

  // Coalesce the point-cloud pick to ~10 Hz. Each pick re-renders the whole
  // scene twice AND does a synchronous GPU readback (see PointCloudBVH), so at
  // the native pointermove rate (~60 Hz) it stalls the main thread and tanks
  // FPS. A coordinate read-out does not need per-pixel updates — 10 Hz reads
  // instantly while cutting the pick cost ~85%. The trailing timer guarantees
  // the coordinate still settles on the final position after the pointer stops.
  private static readonly PICK_INTERVAL_MS = 100;
  private _lastPickTs = 0;
  private _pendingCoord: { clientX: number; clientY: number } | null = null;
  private _trailingTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    viewer: Viewer,
    inputHandler: InputHandler,
    callback: (res: Vector3) => void
  ) {
    this._viewer = viewer;
    this._inputHandler = inputHandler;
    this._callback = callback;
  }

  /** Skip the GPU pick while it is both irrelevant and the cause of stutter. */
  private _suppressed(): boolean {
    // camera orbit/pan (existing guard) OR an IFC gizmo drag: in both cases the
    // coordinate read-out is meaningless and the per-move readback is exactly
    // what makes the interaction judder.
    return (
      this._viewer.whileOperation ||
      (this._viewer.designManager?.isDragging() ?? false)
    );
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ) {
    if (this._suppressed()) {
      return;
    }

    if (pointer.eventType !== InputEventType.POINTER_HOVER) {
      return;
    }

    this._pendingCoord = { clientX: event.clientX, clientY: event.clientY };
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const due = this._lastPickTs + CoordTracker.PICK_INTERVAL_MS;
    if (now >= due) {
      this._runPick();
    } else if (this._trailingTimer === null) {
      this._trailingTimer = setTimeout(() => {
        this._trailingTimer = null;
        this._runPick();
      }, due - now);
    }
  }

  private _runPick(): void {
    const coord = this._pendingCoord;
    this._pendingCoord = null;
    if (!coord || this._suppressed()) {
      return;
    }
    this._lastPickTs =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const point = this._viewer.intersectPointCloud(coord);
    const offset = this._viewer.getOffset();
    this._callback(
      point
        ? new Vector3(
            point.x - offset.x,
            point.y - offset.y,
            point.z - offset.z
          )
        : null
    );
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {}
}
